import React from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
} from '@mui/material'
import { AddNewButton } from '~/src/components/atoms/Button/AddNewButton/AddNewButton'
import { EditButton } from '~/src/components/atoms/Button/EditButton/EditButton'
import { formatDate } from '~/src/libs/formatDate'
import { formatMoney } from '~/src/utils/formatMoney'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

interface IFreight {
  id: string
  departurePort: string
  destinationPort: string
  twentyFt: number
  fortyFt: number
  estimatedDate?: string
}

interface IColumn {
  id: 'departurePort' | 'destinationPort' | '20ft' | '40ft' | 'estimatedDate' | 'edit'
  label: string
  minWidth?: number
  align?: 'right'
  render: (item: IFreight, index: number) => React.ReactNode
}

interface ITableFreightProps {
  title: string
  data: IFreight[]
  isOceanFred?: boolean
}

export const TableFreight: React.FC<ITableFreightProps> = ({
  data,
  title,
  isOceanFred,
}): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const columns: IColumn[] = [
    {
      id: 'departurePort',
      label: t('freight.departurePort'),
      minWidth: 150,
      render: ({ departurePort }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: '#222222',
          }}
        >
          {departurePort}
        </Typography>
      ),
    },
    {
      id: 'destinationPort',
      label: t('freight.destinationPort'),
      minWidth: 200,
      render: ({ destinationPort }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {destinationPort}
        </Typography>
      ),
    },
    {
      id: '20ft',
      label: '20ft',
      minWidth: 150,
      render: ({ twentyFt }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {formatMoney(twentyFt, '¥', 'left')}
        </Typography>
      ),
    },
    {
      id: '40ft',
      label: '40ft',
      minWidth: 150,
      render: ({ fortyFt }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {formatMoney(fortyFt, '¥', 'left')}
        </Typography>
      ),
    },
    {
      id: 'estimatedDate',
      label: isOceanFred ? t('freight.estimatedDate') : '',
      minWidth: isOceanFred ? 150 : 0,
      render: ({ estimatedDate }) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Roboto',
            fontStyle: 'normal',
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.15px',
            color: 'rgba(0, 0, 0, 0.87)',
          }}
        >
          {estimatedDate && formatDate(estimatedDate)}
        </Typography>
      ),
    },
    {
      id: 'edit',
      label: `${t('freight.edit')}`,
      minWidth: 90,
      render: (item) => (
        <EditButton
          text={'freight.edit'}
          handleEdit={() => navigate(`/admin/settings/freight/detail/${item.id}`)}
        />
      ),
    },
  ]

  return (
    <Paper sx={{ width: '100%' }}>
      <Typography
        variant={'h6'}
        sx={{
          fontFamily: 'Roboto',
          fontStyle: 'normal',
          fontWeight: 500,
          fontSize: '20px',
          lineHeight: '32px',
          letterSpacing: '0.15px',
          color: '#000000',
          padding: '24px 16px 16px 16px',
          margin: '0 8px',
          boxShadow: 'inset 0px -1px 0px rgba(0, 0, 0, 0.12)',
        }}
      >
        {title}
      </Typography>
      <TableContainer sx={{ padding: ' 0 8px' }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth, background: '#FFFFFF' }}
                >
                  <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: '24px',
                        fontStyle: 'normal',
                        fontWeight: 'medium',
                      }}
                    >
                      {column.label}
                    </Typography>
                    <Typography
                      sx={{
                        width: '2px',
                        backgroundColor: `${
                          index === columns.length - 1 || (index === 4 && !isOceanFred)
                            ? 'white'
                            : '#E0E0E0'
                        }`,
                        height: 14,
                      }}
                    />
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => {
              return (
                <TableRow hover role="checkbox" tabIndex={-1} key={index}>
                  {columns.map((column, i) => {
                    return (
                      <TableCell key={i} align={column.align} sx={{ padding: '15.5px 16px' }}>
                        {column.render(item, index)}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ textAlign: 'right', padding: '16px' }}>
        <AddNewButton text={'add_new_button.add_new'} />
      </Box>
    </Paper>
  )
}
